import { AmortizationTable } from './AmortizationTable';
import { formatCOP, formatMonths, formatDate } from '@/lib/format';
import type { ProjectionResult } from '@shared/types';

/**
 * Maneja los 4 status del motor. Con NEVER_AMORTIZES muestra un banner
 * rojo y NADA más (sin tabla, sin gráfico) — es un requisito de UI, no
 * solo del motor: la app debe decirlo claro, no dejar que el usuario
 * interprete una tabla vacía.
 */
export function ProjectionSummary({ result }: { result: ProjectionResult }) {
  if (result.status === 'ALREADY_PAID') {
    return (
      <div className="rounded-lg border border-income/30 bg-income/10 p-4 text-sm text-income">
        Esta deuda ya está pagada. 🎉
      </div>
    );
  }

  if (result.status === 'NEVER_AMORTIZES') {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-medium">Con este abono la deuda nunca se paga.</p>
        <p className="mt-1">
          Los intereses del mes son <strong>{formatCOP(result.monthlyInterest)}</strong>. Abona al menos{' '}
          <strong>{formatCOP(result.minimumPayment)}</strong> para que empiece a bajar el saldo.
        </p>
      </div>
    );
  }

  if (result.status === 'EXCEEDS_HORIZON') {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-medium">La deuda tarda más de 50 años en pagarse con este abono.</p>
        <p className="mt-1">Saldo restante simulado: {formatCOP(result.remainingBalance)}. Considera un abono mayor.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Se paga en</p>
          <p className="text-lg font-semibold">{formatMonths(result.months)}</p>
          <p className="text-xs text-muted-foreground">{formatDate(result.payoffDate)}</p>
        </div>
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Total intereses</p>
          <p className="text-lg font-semibold text-debt">{formatCOP(result.totalInterest)}</p>
        </div>
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Total pagado</p>
          <p className="text-lg font-semibold">{formatCOP(result.totalPaid)}</p>
        </div>
      </div>
      <AmortizationTable schedule={result.schedule} />
    </div>
  );
}
